import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { CentBasedInputCurrency, InputLoader, Label, Tag, Text, Wrapper } from 'components';
import { lineHeight, theme, toRem } from 'design';
import { Size } from 'design/types';
import { IPOSSegmentTotals, IPOSTicket, SourcePaymentType, SourceType } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';

export interface IEmployeeListItemProps {
  totals: IPOSSegmentTotals;
  onTotalsChange: (source: SourceType, type: SourcePaymentType, value: number) => void;
  dataCy?: string;
  subText?: string | React.ReactNode;
  title: string;
  isVirtual?: boolean;
  isOwnerRule?: boolean;
  creditCardFeeEnabled?: boolean;
  creditCardPercentFee?: number;
  overrideTickets?: IPOSTicket[];
  headerError?: string;
  inactiveSegment?: boolean;
  isLoading?: boolean;
}

const HeaderErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  background: ${theme.color.warning[25]};
  border: 1px solid ${theme.color.neutral[200]};
  border-bottom: none;
  border-radius: ${toRem(8)} ${toRem(8)} 0 0;
  padding: ${toRem(theme.space.sm)};
  width: 100%;
`;

const OverrideCheckWarning = styled.div`
  display: flex;
  justify-content: center;
  background: ${theme.color.warning[25]};
  padding: ${toRem(theme.space.sm)};
  width: 100%;
  box-shadow: 0px -1px 0px 0px ${theme.color.neutral[300]} inset;
  border-radius: ${toRem(8)} ${toRem(8)} 0 0;

  p {
    text-align: center;
  }
`;

const ListItemLayout = styled.li<{ caution?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  background: ${theme.color.neutral[50]};
  border: 1px solid ${theme.color.neutral[200]};
  border-radius: ${({ caution }) => (caution ? `0 0 ${toRem(8)} ${toRem(8)}` : toRem(8))};
  width: 100%;

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: column nowrap;
    justify-content: space-between;
    align-items: center;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  padding: ${toRem(theme.space.lg)};
`;

const SubTextStyles = css`
  font-size: ${toRem(theme.font.size.xs)};
  font-weight: ${theme.font.weight.medium};
  color: ${theme.color.neutral[500]};
`;

const TitleRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  width: 100%;
`;

const TitleContainer = styled.div<{ alignItems?: string }>`
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: ${toRem(theme.space.lg)};
  align-items: ${({ alignItems }) => alignItems || ''};

  .title {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
    margin-bottom: ${toRem(theme.space.xs)};
    word-break: break-word;
  }

  .sub-text {
    ${SubTextStyles}
  }
`;

const StyledTag = styled(Tag)`
  background-color: ${theme.color.neutral[200]};
  margin-bottom: ${toRem(theme.space.xs)};
`;

const TipEntryContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  width: 100%;

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row nowrap;
    align-items: flex-end;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;

  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.lg)};
  }

  label {
    ${SubTextStyles}
    margin-bottom: ${toRem(theme.space.xs)};
  }

  .error > input {
    border: 1px solid ${theme.color.danger[50]};
  }

  .net-amount input {
    font-weight: ${theme.font.weight.bold};
    padding: 0 ${toRem(theme.space.sm)} !important;
  }

  /* Desktop styles */
  @media (min-width: ${theme.breakpoint.med}px) {
    min-width: ${toRem(80)};

    &:not(:last-child) {
      margin-right: ${toRem(theme.space.xs)};
      margin-bottom: 0;
    }

    .desktop-loader {
      width: ${toRem(100)} !important;
    }
  }
`;

const ItalicizedText = styled(Text)`
  font-style: italic;
`;

const PercentSpan = styled.span`
  font-size: ${toRem(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.regular};
  color: ${theme.color.neutral[500]} !important;
  margin-left: ${toRem(theme.space.sm)};
`;

const noop = () => {};

const EmployeeListItem: React.FC<IEmployeeListItemProps> = ({
  totals,
  dataCy,
  headerError,
  inactiveSegment,
  isLoading,
  onTotalsChange,
  subText,
  title,
  overrideTickets,
  creditCardFeeEnabled,
  creditCardPercentFee = 0,
  isVirtual = false,
  isOwnerRule = false,
}) => {
  const renderSubText = () => {
    if (!subText) return null;
    if (typeof subText !== 'string') return subText;
    return (
      <Text className="sub-text" size="xsmall">
        {subText}
      </Text>
    );
  };

  const renderOverrideCheckWarningMessage = useMemo(() => {
    if (!overrideTickets?.length) return <></>;

    const total = overrideTickets.reduce((acc, ticket) => acc + ticket.totals.net, 0);
    const ticketDisplayIds = overrideTickets.map((ticket) => `#${ticket.displayId}`);
    const message = `Amount shown excludes ${centsToDollarsDisplay(total)} from check${ticketDisplayIds.length > 1 ? 's' : ''} ${ticketDisplayIds.join(', ')}.`;

    return (
      <OverrideCheckWarning data-cy="tip-amount-split-check-warning">
        <Text size="small" fontWeight="medium">
          {message}
        </Text>
      </OverrideCheckWarning>
    );
  }, [overrideTickets]);

  return isLoading ? (
    <ListItemLayout>
      <TitleContainer>
        <Skeleton className="title" width={150} />
        <Skeleton width={100} />
      </TitleContainer>
      <TipEntryContainer>
        <InputContainer>
          <Label>
            <Skeleton width={50} />
          </Label>
          <InputLoader className="desktop-loader" />
        </InputContainer>
        <InputContainer>
          <Label>
            <Skeleton width={50} />
          </Label>
          <InputLoader className="desktop-loader" />
        </InputContainer>
      </TipEntryContainer>
    </ListItemLayout>
  ) : (
    <>
      {headerError && (
        <HeaderErrorContainer data-cy="tip-amount-header-error">
          <Text size="small" fontWeight="medium">
            {headerError}
          </Text>
        </HeaderErrorContainer>
      )}
      <ListItemLayout data-cy={dataCy} caution={!!headerError}>
        {renderOverrideCheckWarningMessage}
        <MainContent>
          <TitleRow>
            <TitleContainer>
              {(isOwnerRule || isVirtual) && (
                <Wrapper>
                  {isOwnerRule && <StyledTag label="Owner Rule" size={Size.SMALL} dataCy="owner-rule-tag" />}
                  {isVirtual && <StyledTag label="Virtual" size={Size.SMALL} dataCy="virtual-tag" />}
                </Wrapper>
              )}
              <Text className="title" fontWeight="semibold">
                {title}
              </Text>
              {renderSubText()}
            </TitleContainer>
            <TitleContainer alignItems="flex-end">
              <Text className="title" fontWeight="semibold">
                {centsToDollarsDisplay(totals.net)}
              </Text>
              <Text className="sub-text" size="xsmall">
                Net
              </Text>
            </TitleContainer>
          </TitleRow>
          <TipEntryContainer>
            {!inactiveSegment ? (
              <>
                <InputContainer>
                  <Label>CC Tips</Label>
                  <CentBasedInputCurrency
                    data-cy="calculator-tip-input type-cc"
                    value={totals.tip.creditCard?.toString()}
                    onChange={(value: string) => onTotalsChange(SourceType.TIP, SourcePaymentType.CREDIT, parseInt(value, 10))}
                    allowZero
                  />
                </InputContainer>
                <InputContainer>
                  <Label>CC Autograts</Label>
                  <CentBasedInputCurrency
                    data-cy="calculator-tip-input type-cc-autograts"
                    value={totals.autoGratuity.creditCard?.toString()}
                    onChange={(value: string) => onTotalsChange(SourceType.AUTO_GRATUITIES, SourcePaymentType.CREDIT, parseInt(value, 10))}
                    allowZero
                  />
                </InputContainer>
                <InputContainer>
                  <Label>Cash Tips</Label>
                  <CentBasedInputCurrency data-cy="calculator-tip-input type-cash" value={totals.tip.cash.toString()} onChange={(value: string) => onTotalsChange(SourceType.TIP, SourcePaymentType.CASH, parseInt(value, 10))} allowZero />
                </InputContainer>
                <InputContainer>
                  <Label>Cash Autograts</Label>
                  <CentBasedInputCurrency
                    data-cy="calculator-tip-input type-cash-autograts"
                    value={totals.autoGratuity.cash.toString()}
                    onChange={(value: string) => onTotalsChange(SourceType.AUTO_GRATUITIES, SourcePaymentType.CASH, parseInt(value, 10))}
                    allowZero
                  />
                </InputContainer>
                {creditCardFeeEnabled && (
                  <InputContainer>
                    <Label>
                      CC Fee <PercentSpan>{`${creditCardPercentFee}%`}</PercentSpan>
                    </Label>
                    <CentBasedInputCurrency data-cy="calculator-tip-input type-cc-fee" value={(totals.tip.creditCardFee + totals.autoGratuity.creditCardFee).toString()} onChange={noop} disabled allowZero />
                  </InputContainer>
                )}
              </>
            ) : (
              <ItalicizedText size="small" data-cy="inactive-segment-label">
                No hours worked in this day segment.
              </ItalicizedText>
            )}
          </TipEntryContainer>
        </MainContent>
      </ListItemLayout>
    </>
  );
};

export default EmployeeListItem;
